import PeopleIcon from '@mui/icons-material/People';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import StoreMallDirectoryIcon from '@mui/icons-material/StoreMallDirectory';
import { Box, Typography } from '@mui/material';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const DOUBLE_CLICK_MS = 350;

const getActiveKey = (pathname) => {
  if (pathname.startsWith('/dashboard/clients')) {
    return 'clients';
  }
  if (pathname.startsWith('/dashboard/storages')) {
    return 'storages';
  }
  if (pathname.startsWith('/dashboard/app')) {
    return 'statistics';
  }
  return null;
};

export default function PinnedBottomNav() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lastClientsClickRef = useRef(0);
  const activeKey = getActiveKey(pathname);

  const handleClientsClick = () => {
    const now = Date.now();

    if (now - lastClientsClickRef.current < DOUBLE_CLICK_MS) {
      navigate('/dashboard/clients/add');
      lastClientsClickRef.current = 0;
      return;
    }

    lastClientsClickRef.current = now;
    navigate('/dashboard/clients');
  };

  const buttons = [
    {
      key: 'clients',
      label: t('MIJOZLAR'),
      icon: PeopleIcon,
      onClick: handleClientsClick
    },
    {
      key: 'statistics',
      label: t('STATISTIKA'),
      icon: SignalCellularAltIcon,
      onClick: () => navigate('/dashboard/app')
    },
    {
      key: 'storages',
      label: t('OMBORXONA'),
      icon: StoreMallDirectoryIcon,
      onClick: () => navigate('/dashboard/storages')
    }
  ];

  return (
    <Box
      component="nav"
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1200,
        bgcolor: 'transparent',
        pointerEvents: 'none',
        px: 2,
        py: 1.5,
        pb: 'calc(12px + env(safe-area-inset-bottom, 0px))'
      }}>
      <Box
        display="flex"
        alignItems="stretch"
        gap={1.5}
        maxWidth={960}
        mx="auto"
        sx={{ pointerEvents: 'auto' }}>
        {buttons.map((button) => {
          const isActive = activeKey === button.key;
          const Icon = button.icon;

          return (
            <Box
              key={button.key}
              role="button"
              tabIndex={0}
              onClick={button.onClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  button.onClick();
                }
              }}
              sx={{
                flex: 1,
                py: 1.25,
                px: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                cursor: 'pointer',
                userSelect: 'none',
                borderRadius: 2,
                border: '1px solid',
                borderColor: isActive ? 'primary.main' : 'divider',
                bgcolor: isActive ? 'primary.lighter' : 'background.paper',
                boxShadow: (theme) => theme.shadows[2],
                color: isActive ? 'primary.main' : 'text.secondary',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: isActive ? 'primary.lighter' : 'grey.100',
                  borderColor: isActive ? 'primary.main' : 'grey.400'
                },
                '&:active': {
                  opacity: 0.85
                }
              }}>
              <Icon
                sx={{
                  fontSize: 22,
                  color: isActive ? 'primary.main' : 'text.secondary'
                }}
              />
              <Typography
                variant="caption"
                fontWeight={isActive ? 700 : 500}
                fontSize="0.75rem"
                lineHeight={1.2}
                textAlign="center"
                noWrap
                sx={{ color: isActive ? 'primary.main' : 'text.secondary' }}>
                {button.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
